/**
 * Workspace Role Constants
 *
 * Centralized configuration for workspace roles including
 * icons, colors, and avatar utilities.
 *
 * @module constants/roles
 */

import { IconShield, IconEdit, IconEye } from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';
import type { WorkspaceRole } from '@/types/workspace';

// ===== ROLE CONFIGURATION =====

/** Configuration for a single workspace role */
export interface RoleConfig {
  /** Icon component for the role badge */
  readonly icon: TablerIcon;
  /** Background color (CSS variable) */
  readonly bgColor: string;
  /** Text color (CSS variable) */
  readonly textColor: string;
  /** Display label (fallback, components should use i18n) */
  readonly label: string;
}

/** Visual configuration for each workspace role */
export const ROLE_CONFIG: Readonly<Record<WorkspaceRole, RoleConfig>> = {
  Admin: {
    icon: IconShield,
    bgColor: 'var(--mantine-color-red-0)',
    textColor: 'var(--mantine-color-red-7)',
    label: 'Administrator',
  },
  Editor: {
    icon: IconEdit,
    bgColor: 'var(--mantine-color-blue-0)',
    textColor: 'var(--mantine-color-blue-6)',
    label: 'Editor',
  },
  Viewer: {
    icon: IconEye,
    bgColor: 'var(--mantine-color-gray-1)',
    textColor: 'var(--mantine-color-gray-7)',
    label: 'Viewer',
  },
} as const;

// ===== AVATAR CONFIGURATION =====

/** Color palette for user avatars (deterministic based on identifier) */
export const AVATAR_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f43f5e',
  '#f97316',
  '#22c55e',
  '#14b8a6',
  '#0ea5e9',
] as const;

/** Maximum number of avatars visible before showing "+N" overflow */
export const MAX_VISIBLE_AVATARS = 4;

/**
 * Get a deterministic avatar color based on a string identifier.
 * Uses the first character's char code to consistently assign colors.
 */
export function getAvatarColor(identifier: string): string {
  const charCode = identifier.charCodeAt(0) || 0;
  const index = charCode % AVATAR_COLORS.length;
  return AVATAR_COLORS[index] ?? AVATAR_COLORS[0];
}

/**
 * Extract initials from an email address for avatar display.
 * For "john.doe@example.com" returns "JD".
 * For "admin@example.com" returns "AD".
 */
export function getEmailInitials(email: string): string {
  const name = email.split('@')[0] ?? '';

  if (name.includes('.')) {
    return name
      .split('.')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  return name.slice(0, 2).toUpperCase();
}

/** Mantine color names for Avatar component (deterministic based on identifier) */
export const MANTINE_AVATAR_COLORS = [
  'blue',
  'teal',
  'green',
  'orange',
  'red',
  'violet',
  'indigo',
  'cyan',
  'pink',
  'grape',
] as const;

/**
 * Get a deterministic Mantine color name for Avatar component.
 * Uses full string hash for better distribution.
 */
export function getMantineAvatarColor(identifier: string): string {
  const hash = identifier
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return MANTINE_AVATAR_COLORS[hash % MANTINE_AVATAR_COLORS.length] ?? 'indigo';
}

/**
 * Extract initials from a display name with email fallback.
 * For "John Doe" returns "JD". For null + "john@example.com" returns "J".
 */
export function getMemberInitials(
  displayName: string | null,
  email: string
): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]?.charAt(0) ?? ''}${parts[1]?.charAt(0) ?? ''}`.toUpperCase();
    }
    return displayName.charAt(0).toUpperCase();
  }
  return email.charAt(0).toUpperCase();
}

// ===== WORKSPACE FORM LIMITS =====

/** Maximum length for workspace name input */
export const WORKSPACE_NAME_FORM_MAX_LENGTH = 100;

/** Maximum length for workspace description input */
export const WORKSPACE_DESCRIPTION_FORM_MAX_LENGTH = 500;
